import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  IconButton,
  Autocomplete // Cambiamos Select por Autocomplete
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { getPisos, createPiso, deletePiso, getTrabajos, createTrabajo, deleteTrabajo } from '../services/api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminPanel = () => {
  const [tabValue, setTabValue] = useState(0);
  const [pisos, setPisos] = useState([]);
  const [trabajos, setTrabajos] = useState([]);
  const [openPisoDialog, setOpenPisoDialog] = useState(false);
  const [openTrabajoDialog, setOpenTrabajoDialog] = useState(false);
  const [nuevoPiso, setNuevoPiso] = useState({ nombre: '', direccion: '', duracion_estimada: '' });
  const [nuevoTrabajo, setNuevoTrabajo] = useState({ 
    piso_id: '', 
    piso_nombre: '', 
    fecha: '', 
    duracion: '', 
    entrada_salida: false 
  });
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    loadPisos();
    loadTrabajos();
  }, []);

  const loadPisos = async () => {
    try {
      const response = await getPisos();
      setPisos(response.data);
    } catch (error) {
      console.error('Error loading pisos:', error);
    }
  };

  const loadTrabajos = async () => {
    try {
      const response = await getTrabajos();
      setTrabajos(response.data);
    } catch (error) {
      console.error('Error loading trabajos:', error);
    }
  };

  const handleAddPiso = async () => {
    try {
      await createPiso({
        ...nuevoPiso,
        duracion_estimada: parseInt(nuevoPiso.duracion_estimada)
      });
      setOpenPisoDialog(false);
      setNuevoPiso({ nombre: '', direccion: '', duracion_estimada: '' });
      loadPisos();
    } catch (error) {
      console.error('Error adding piso:', error);
      alert('Error al añadir piso: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddTrabajo = async () => {
    try {
      // Verificar que todos los campos estén completos
      if (!nuevoTrabajo.piso_id || !nuevoTrabajo.fecha || !nuevoTrabajo.duracion) {
        alert('Por favor, completa todos los campos');
        return;
      }

      await createTrabajo({
        piso_id: parseInt(nuevoTrabajo.piso_id),
        fecha: nuevoTrabajo.fecha,
        duracion: parseInt(nuevoTrabajo.duracion),
        entrada_salida: nuevoTrabajo.entrada_salida
      });
      
      setOpenTrabajoDialog(false);
      setNuevoTrabajo({ 
        piso_id: '', 
        piso_nombre: '', 
        fecha: '', 
        duracion: '', 
        entrada_salida: false 
      });
      loadTrabajos();
    } catch (error) {
      console.error('Error adding trabajo:', error);
      alert('Error al asignar trabajo: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeletePiso = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este piso?')) {
      try {
        await deletePiso(id);
        loadPisos();
      } catch (error) {
        console.error('Error deleting piso:', error);
      }
    }
  };

  const handleDeleteTrabajo = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este trabajo?')) {
      try {
        await deleteTrabajo(id);
        loadTrabajos();
      } catch (error) {
        console.error('Error deleting trabajo:', error);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Filtrar pisos basado en la búsqueda
  const filteredPisos = pisos.filter(piso =>
    piso.nombre.toLowerCase().includes(searchText.toLowerCase()) ||
    piso.direccion.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Panel de Administración
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Gestión de Pisos" />
          <Tab label="Asignar Trabajos" />
          <Tab label="Calendario Semanal" />
        </Tabs>
      </Box>

      {/* Pestaña 1: Gestión de Pisos */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenPisoDialog(true)}
          >
            Añadir Nuevo Piso
          </Button>
          
          <TextField
            label="Buscar pisos..."
            variant="outlined"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Dirección</TableCell>
                <TableCell>Duración (min)</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPisos.map((piso) => (
                <TableRow key={piso.id}>
                  <TableCell>
                    <Typography variant="subtitle1">
                      <strong>{piso.nombre}</strong>
                    </Typography>
                  </TableCell>
                  <TableCell>{piso.direccion}</TableCell>
                  <TableCell>{piso.duracion_estimada}</TableCell>
                  <TableCell>
                    <IconButton color="error" onClick={() => handleDeletePiso(piso.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredPisos.length === 0 && searchText && (
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            No se encontraron pisos que coincidan con "{searchText}"
          </Typography>
        )}

        {/* Diálogo para añadir piso */}
        <Dialog open={openPisoDialog} onClose={() => setOpenPisoDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Añadir Nuevo Piso</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nombre del piso"
              fullWidth
              variant="outlined"
              value={nuevoPiso.nombre}
              onChange={(e) => setNuevoPiso({...nuevoPiso, nombre: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Dirección"
              fullWidth
              variant="outlined"
              multiline
              rows={2}
              value={nuevoPiso.direccion}
              onChange={(e) => setNuevoPiso({...nuevoPiso, direccion: e.target.value})}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Duración estimada (minutos)"
              type="number"
              fullWidth
              variant="outlined"
              value={nuevoPiso.duracion_estimada}
              onChange={(e) => setNuevoPiso({...nuevoPiso, duracion_estimada: e.target.value})}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPisoDialog(false)}>Cancelar</Button>
            <Button 
              onClick={handleAddPiso} 
              variant="contained"
              disabled={!nuevoPiso.nombre || !nuevoPiso.direccion || !nuevoPiso.duracion_estimada}
            >
              Añadir
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Pestaña 2: Asignar Trabajos */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenTrabajoDialog(true)}
          >
            Asignar Trabajo
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Piso</TableCell>
                <TableCell>Duración</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trabajos.map((trabajo) => (
                <TableRow key={trabajo.id}>
                  <TableCell>{trabajo.fecha}</TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {trabajo.piso?.nombre}
                    </Typography>
                    <Typography variant="caption" display="block">
                      {trabajo.piso?.direccion}
                    </Typography>
                  </TableCell>
                  <TableCell>{trabajo.duracion} min</TableCell>
                  <TableCell>
                    {trabajo.entrada_salida && (
                      <Chip label="Entrada/Salida" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton color="error" onClick={() => handleDeleteTrabajo(trabajo.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Diálogo para asignar trabajo */}
        <Dialog open={openTrabajoDialog} onClose={() => setOpenTrabajoDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Asignar Trabajo</DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Fecha (YYYY-MM-DD)"
              fullWidth
              variant="outlined"
              value={nuevoTrabajo.fecha}
              onChange={(e) => setNuevoTrabajo({...nuevoTrabajo, fecha: e.target.value})}
              placeholder="2024-01-15"
              sx={{ mb: 2 }}
              helperText="Formato: Año-Mes-Día (ej: 2024-01-15)"
            />
            
            <Autocomplete
              options={pisos}
              getOptionLabel={(option) => `${option.nombre} - ${option.direccion}`}
              value={pisos.find(p => p.id === parseInt(nuevoTrabajo.piso_id)) || null}
              onChange={(event, newValue) => {
                setNuevoTrabajo({
                  ...nuevoTrabajo,
                  piso_id: newValue ? newValue.id : '',
                  piso_nombre: newValue ? newValue.nombre : '',
                  duracion: newValue ? newValue.duracion_estimada : ''
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Buscar piso..."
                  variant="outlined"
                  margin="dense"
                  sx={{ mb: 2 }}
                />
              )}
            />
            
            <TextField
              margin="dense"
              label="Duración (minutos)"
              type="number"
              fullWidth
              variant="outlined"
              value={nuevoTrabajo.duracion}
              onChange={(e) => setNuevoTrabajo({...nuevoTrabajo, duracion: e.target.value})}
              sx={{ mb: 2 }}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                id="entrada_salida"
                checked={nuevoTrabajo.entrada_salida}
                onChange={(e) => setNuevoTrabajo({...nuevoTrabajo, entrada_salida: e.target.checked})}
              />
              <label htmlFor="entrada_salida" style={{ marginLeft: '8px' }}>
                Marcar como Entrada/Salida
              </label>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenTrabajoDialog(false)}>Cancelar</Button>
            <Button 
              onClick={handleAddTrabajo} 
              variant="contained"
              disabled={!nuevoTrabajo.piso_id || !nuevoTrabajo.fecha || !nuevoTrabajo.duracion}
            >
              Asignar
            </Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      {/* Pestaña 3: Calendario Semanal */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>
          Vista Semanal de Trabajos
        </Typography>
        <Grid container spacing={2}>
          {[...Array(7)].map((_, index) => {
            const fecha = new Date();
            fecha.setDate(fecha.getDate() + index);
            const fechaStr = fecha.toISOString().split('T')[0];
            const trabajosDia = trabajos.filter(t => t.fecha === fechaStr);

            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      {fecha.toLocaleDateString('es-ES', { weekday: 'long' })}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {fecha.toLocaleDateString('es-ES')}
                    </Typography>
                    
                    <Box sx={{ mt: 1 }}>
                      {trabajosDia.map((trabajo) => (
                        <Box key={trabajo.id} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                          <Typography variant="body2">
                            <strong>{trabajo.piso?.nombre}</strong>
                          </Typography>
                          <Typography variant="caption" display="block">
                            {trabajo.duracion} min
                            {trabajo.entrada_salida && (
                              <Chip label="E/S" color="error" size="small" sx={{ ml: 1 }} />
                            )}
                          </Typography>
                        </Box>
                      ))}
                      
                      {trabajosDia.length === 0 && (
                        <Typography variant="caption" color="textSecondary">
                          Sin trabajos programados
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>
    </Paper>
  );
};

export default AdminPanel;